import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { StatusBadge } from "./Bits";
import { api, errMsg, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaymentBadge, PaymentDialog, CreditLimitEditor, CustomerPaymentDialog, OpeningEditor } from "./Payments";
import { Button } from "./ui/button";
import { FileText, Wallet } from "lucide-react";
import { downloadFile } from "../lib/api";

const fmtDate = (s) => new Date(s).toLocaleDateString("tr-TR");

const PaymentCell = ({ o, onOpen, m = "" }) => {
  if (o.status === "İptal") return <span className="text-xs text-slate-400">-</span>;
  const rem = o.grand_total - (o.paid_amount || 0);
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="text-right">
        <PaymentBadge status={o.payment_status} testId={`${m}payment-status-${o.order_no}`} />
        {o.payment_status === "partial" && <p className="mt-0.5 text-[10px] text-slate-500">Kalan {money(rem)}</p>}
      </div>
      <button type="button" data-testid={`${m}payment-open-${o.order_no}`} onClick={() => onOpen(o)}
        className="rounded-md bg-slate-950 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-600">Tahsilat</button>
    </div>
  );
};

const Stat = ({ label, value, id, cls = "" }) => (
  <div className={`rounded-xl border p-4 ${cls}`}>
    <p className="font-mono text-[10px] uppercase tracking-wider opacity-70">{label}</p>
    <p className="mt-1 font-display text-xl font-bold" data-testid={id}>{value}</p>
  </div>
);

export const CustomerDetailDialog = ({ customer, onClose, onChanged = () => {} }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [custPay, setCustPay] = useState(false);
  const load = () => api.get(`/customers/${customer.id}/orders`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setData(null); if (customer) load(); }, [customer]); // eslint-disable-line

  const onPaid = (o) => { setPayOrder(o); load(); onChanged(); };
  const pdf = () => downloadFile(`/customers/${customer.id}/statement`, `ekstre_${customer.name}.pdf`).catch((e) => toast.error(errMsg(e)));
  const cr = data?.credit;
  const over = cr && cr.limit > 0 && cr.balance > cr.limit;

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-3xl rounded-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="customer-detail-dialog">
        {customer && <>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{customer.name}</DialogTitle>
            <p className="text-sm text-slate-500">{[customer.phone, customer.address].filter(Boolean).join(" · ")} {customer.created_by_name && <>· Plasiyer: <b>{customer.created_by_name}</b></>}</p>
          </DialogHeader>
          {!data ? <p className="py-8 text-center text-sm text-slate-500">Yükleniyor...</p> : <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Fatura Sayısı" value={data.summary.count} id="customer-summary-count" cls="border-slate-200" />
              <Stat label={data.summary.opening ? "Toplam Borç (Devir dahil)" : "Toplam Satış"} value={money(data.summary.total)} id="customer-summary-total" cls="border-slate-200" />
              <Stat label="Ödenen" value={money(data.summary.paid)} id="customer-summary-paid" cls="border-emerald-200 bg-emerald-50 text-emerald-900" />
              <Stat label="Cari Bakiye" value={money(data.summary.balance)} id="customer-summary-balance" cls="border-slate-950 bg-slate-950 text-white" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="customer-payment-open-button" disabled={!(data.summary.balance > 0)} onClick={() => setCustPay(true)} className="bg-emerald-600 hover:bg-emerald-700"><Wallet className="mr-2 h-4 w-4" />Toplam Cariden Tahsilat</Button>
              <Button data-testid="customer-statement-pdf-button" variant="outline" onClick={pdf}><FileText className="mr-2 h-4 w-4" />Cari Ekstre PDF</Button>
            </div>
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm ${over ? "border-rose-300 bg-rose-50 text-rose-800" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="customer-credit-info">
              <span>Cari Limit: <b data-testid="customer-credit-limit">{cr.limit > 0 ? money(cr.limit) : "Limitsiz"}</b></span>
              <span>{user.role === "admin" ? "Toplam Açık Cari" : "Tüm Plasiyerler Açık Cari (limit için)"}: <b data-testid="customer-credit-balance">{money(cr.balance)}</b></span>
              {cr.limit > 0 && <span>Kullanılabilir: <b data-testid="customer-credit-available">{money(cr.available)}</b></span>}
            </div>
            {data.summary.opening !== 0 && (
              <p className="text-sm text-slate-600" data-testid="customer-opening-info">Devir bakiyesi: <b>{money(data.summary.opening)}</b> · Devirden tahsil edilen: <b>{money(data.summary.opening_paid)}</b></p>
            )}
            {user.role === "admin" && <CreditLimitEditor customer={customer} credit={cr} onSaved={load} />}
            {user.role === "admin" && <OpeningEditor testId="customer-opening" url={`/customers/${customer.id}/opening`} value={data.summary.opening} onSaved={() => { load(); onChanged(); }} />}
            <div className="space-y-2 sm:hidden">
              {data.orders.map((o) => (
                <div key={o.id} data-testid={`customer-order-card-${o.order_no}`} className={`rounded-lg border border-slate-200 p-3 ${o.status === "İptal" ? "opacity-50" : ""}`}>
                  <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold">{o.order_no}</span><StatusBadge status={o.status} /></div>
                  <p className="mt-1 text-xs text-slate-500">{fmtDate(o.created_at)} · {o.marketer_name}</p>
                  <div className="mt-2 flex items-center justify-between"><span className="font-display font-bold">{money(o.grand_total)}</span><PaymentCell m="m-" o={o} onOpen={setPayOrder} /></div>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto rounded-lg border border-slate-200 sm:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase text-slate-500">
                  <tr><th className="p-3">Fatura No</th><th className="p-3">Tarih</th><th className="p-3">Plasiyer</th><th className="p-3">Durum</th><th className="p-3 text-right">Tutar</th><th className="p-3 text-right">Ödeme</th></tr>
                </thead>
                <tbody>
                  {data.orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500" data-testid="customer-orders-empty">Bu müşteriye ait fatura yok</td></tr>}
                  {data.orders.map((o) => (
                    <tr key={o.id} data-testid={`customer-order-row-${o.order_no}`} className={`border-t border-slate-100 ${o.status === "İptal" ? "opacity-50" : ""}`}>
                      <td className="p-3 font-mono font-semibold whitespace-nowrap">{o.order_no}</td>
                      <td className="p-3 whitespace-nowrap text-slate-500">{fmtDate(o.created_at)}</td>
                      <td className="p-3 text-slate-600">{o.marketer_name}</td>
                      <td className="p-3"><StatusBadge status={o.status} /></td>
                      <td className="p-3 text-right font-display font-bold whitespace-nowrap">{money(o.grand_total)}</td>
                      <td className="p-3 text-right"><PaymentCell o={o} onOpen={setPayOrder} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>}
        </>}
      </DialogContent>
      {customer && <CustomerPaymentDialog open={custPay} customer={customer} balance={data?.summary.balance || 0} onClose={() => setCustPay(false)} onDone={() => { load(); onChanged(); }} />}
      <PaymentDialog order={payOrder} onClose={() => setPayOrder(null)} onChanged={onPaid} />
    </Dialog>
  );
};
